import type { Locale } from '@/types'

const MONTHS: Record<Locale, Array<string>> = {
    de: ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'],
    fr: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'],
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
}

const TUNIS_OFFSET_MS = 60 * 60 * 1000

const pad = (value: number): string => String(value).padStart(2, '0')

interface DateParts {
    year: number
    month: number
    day: number
    hours: number
    minutes: number
}

const toParts = (value: string): DateParts | null => {
    if (!value) return null

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number)

        return { year, month, day, hours: 0, minutes: 0 }
    }

    const time = Date.parse(value)

    if (Number.isNaN(time)) return null

    const shifted = new Date(time + TUNIS_OFFSET_MS)

    return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
        hours: shifted.getUTCHours(),
        minutes: shifted.getUTCMinutes()
    }
}

export const formatDate = (value: string | null | undefined, locale: Locale = 'de'): string => {
    const parts = value ? toParts(value) : null

    if (!parts) return ''

    const separator = locale === 'de' ? '.' : '/'

    return `${pad(parts.day)}${separator}${pad(parts.month)}${separator}${parts.year}`
}

export const formatDateLong = (value: string | null | undefined, locale: Locale = 'de'): string => {
    const parts = value ? toParts(value) : null

    if (!parts) return ''

    const month = MONTHS[locale][parts.month - 1]

    if (locale === 'de') return `${parts.day}. ${month} ${parts.year}`
    if (locale === 'en') return `${parts.day} ${month} ${parts.year}`

    return `${parts.day} ${month} ${parts.year}`
}

export const formatDateTime = (value: string | null | undefined, locale: Locale = 'de'): string => {
    const parts = value ? toParts(value) : null

    if (!parts) return ''

    return `${formatDate(value, locale)} ${pad(parts.hours)}:${pad(parts.minutes)}`
}

const groupThousands = (value: number, separator: string): string => {
    const [whole, fraction] = Math.abs(value).toFixed(2).split('.')
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
    const sign = value < 0 ? '-' : ''

    return fraction === '00' ? `${sign}${grouped}` : `${sign}${grouped}${separator === '.' ? ',' : separator === ',' ? '.' : ','}${fraction}`
}

export const formatMoney = (value: number | null | undefined, locale: Locale = 'de'): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return ''

    if (locale === 'en') return `€${groupThousands(value, ',')}`
    if (locale === 'fr') return `${groupThousands(value, ' ')} €`
    if (locale === 'ar') return `${groupThousands(value, ',')} €`

    return `${groupThousands(value, '.')} €`
}

export const nightsBetween = (arrival: string, departure: string): number => {
    const start = Date.parse(`${arrival}T00:00:00Z`)
    const end = Date.parse(`${departure}T00:00:00Z`)

    if (Number.isNaN(start) || Number.isNaN(end)) return 0

    return Math.max(0, Math.round((end - start) / (24 * 60 * 60 * 1000)))
}

export const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const localePath = (locale: Locale, path: string): string => (locale === 'de' ? path : `/${locale}${path}`)
